import { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { PageHeader, Card, CardHeader, StatusPill, PriorityPill, EmptyState, RiskPill } from "../components/primitives";
import { useAuth } from "../lib/auth";
import { formatNGN, formatRelative } from "../lib/format";
import { AlertTriangle, Briefcase, CheckCircle2, Clock, FileText, ShieldCheck, TrendingUp, Activity } from "lucide-react";

export default function MyWork() {
  const { user } = useAuth();
  const [cases, setCases] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [audit, setAudit] = useState([]);

  useEffect(() => {
    api.get("/cases").then(r => setCases(r.data.cases || []));
    api.get("/alerts").then(r => setAlerts(r.data.alerts || []));
    api.get("/audit?limit=15").then(r => setAudit(r.data.entries || []));
  }, []);

  const role = user?.role || "";
  const isSenior = /Chief|Head|MLRO/i.test(role);

  // Role-aware buckets
  const myCases = useMemo(() => cases.filter(c => c.owner === user?.name), [cases, user]);
  const myAlerts = useMemo(() => alerts.filter(a => a.assignee === user?.name), [alerts, user]);
  const oversightP1 = useMemo(() => cases.filter(c => c.priority === "P1"), [cases]);
  const awaitingMlro = useMemo(() => cases.filter(c => /MLRO|Recommend|Awaiting/i.test(c.status)), [cases]);
  const pendingApprovals = useMemo(() => cases.filter(c => c.pending_checker), [cases]);
  const escalations = useMemo(() => alerts.filter(a => a.status === "Escalated"), [alerts]);

  // Synthetic "today's schedule"
  const schedule = [
    { time: "09:00", title: "Daily compliance huddle", type: "Meeting", with: "Compliance team" },
    { time: "10:30", title: "MLRO review — CSE-2026-00128", type: "Case review", with: "Ethan Brennan" },
    { time: "12:00", title: "Sanctions list refresh — OFAC/UN/EU/CBN", type: "Operations", with: "Atom Electron v1.1" },
    { time: "14:00", title: "Threshold change approval", type: "Maker-checker", with: "Marcus Whitfield" },
    { time: "16:00", title: "STR draft review — STR-2026-045", type: "Reporting", with: "Ethan Brennan" },
    { time: "17:00", title: "Board pack QA — May edition", type: "Reporting", with: "Ava Holloway" },
  ];

  // SLA risk (synthetic, based on data)
  const slaAtRisk = oversightP1.slice(0, 3).map((c, i) => ({
    id: c.id, subject: c.subject, hours_left: [4, 11, 23][i] || 24, breach: i === 0,
  }));

  const tiles = [
    { label: "My open cases", value: myCases.length, sub: `${myCases.filter(c=>c.priority==="P1").length} P1`, icon: Briefcase, accent: "text-amber-600" },
    { label: "My open alerts", value: myAlerts.length, sub: `${myAlerts.filter(a=>a.priority==="P1").length} P1`, icon: AlertTriangle, accent: "text-risk-high" },
    { label: "Pending my approval", value: pendingApprovals.length, sub: "Maker-checker queue", icon: CheckCircle2, accent: "text-risk-low" },
    { label: "SLA at risk", value: slaAtRisk.length, sub: `${slaAtRisk.filter(s=>s.breach).length} in breach`, icon: Clock, accent: "text-risk-veryhigh" },
    { label: "Today's STR drafts", value: 3, sub: "1 awaiting MLRO", icon: FileText, accent: "text-info" },
    { label: "Screening latency p95", value: "84ms", sub: "All systems green", icon: ShieldCheck, accent: "text-risk-low" },
  ];

  return (
    <div>
      <PageHeader title={`My Work — ${user?.name || ""}`} subtitle={`${role} · ${new Date().toLocaleDateString("en-GB",{weekday:"long",day:"numeric",month:"long"})}`} sections={["5.5","5.7"]} />

      <div className="p-6 space-y-4">
        {/* Greeting strip */}
        <div className="bg-gradient-to-r from-amber/10 via-white to-white border border-line rounded-2xl p-5 flex items-center justify-between">
          <div>
            <div className="display text-[18px] font-semibold tracking-tight">Good morning, {user?.name?.split(" ")[0]}.</div>
            <div className="text-[12px] text-ink-muted mt-1">You have <span className="font-medium text-ink">{myCases.length + myAlerts.length} items</span> in your queue and <span className="font-medium text-ink">{pendingApprovals.length} approvals</span> waiting on you. {slaAtRisk.filter(s=>s.breach).length > 0 && <span className="text-risk-high">· {slaAtRisk.filter(s=>s.breach).length} SLA breach{slaAtRisk.filter(s=>s.breach).length>1?"es":""}.</span>}</div>
          </div>
          <div className="flex items-center gap-2">
            <Link to="/alerts" className="px-3 py-2 text-[12px] bg-navy text-white rounded-full hover:bg-navy-700 transition-colors">Open alert queue →</Link>
            <Link to="/cases" className="px-3 py-2 text-[12px] border border-line rounded-full hover:bg-bg-hover transition-colors">All cases</Link>
          </div>
        </div>

        {/* Metric tiles */}
        <div className="grid grid-cols-6 gap-3">
          {tiles.map((t, i) => {
            const Icon = t.icon;
            return (
              <div key={i} data-testid={`my-tile-${i}`} className="bg-white border border-line rounded-2xl p-4 hover:shadow-sm transition-shadow">
                <div className="flex items-center justify-between">
                  <div className="text-[10px] uppercase tracking-wider text-ink-subtle">{t.label}</div>
                  <Icon size={14} strokeWidth={1.25} className={t.accent} />
                </div>
                <div className="display text-[24px] font-semibold tabular tracking-tight mt-1.5">{t.value}</div>
                <div className="text-[11px] text-ink-muted mt-0.5">{t.sub}</div>
              </div>
            );
          })}
        </div>

        {/* Row 2: My cases + My alerts */}
        <div className="grid grid-cols-2 gap-3">
          <Card>
            <CardHeader title={`My cases · ${myCases.length}`} right={<Link to="/cases" className="text-[11px] text-amber-600 hover:underline">All →</Link>} />
            <div>
              {myCases.length === 0 ? (
                <EmptyState message={isSenior ? "You don't directly own cases. See oversight queue below." : "No cases assigned to you currently."} />
              ) : myCases.map(c => (
                <Link key={c.id} to={`/cases/${c.id}`} className="flex items-start gap-3 p-3 border-b border-line/50 last:border-0 hover:bg-bg-hover">
                  <PriorityPill priority={c.priority} />
                  <div className="flex-1 min-w-0">
                    <div className="text-[13px] font-medium truncate">{c.subject}</div>
                    <div className="text-[11px] text-ink-subtle">{c.id} · {c.status}</div>
                  </div>
                  <div className="text-right">
                    <div className="text-[12px] tabular font-medium">{formatNGN(c.amount, {short:true})}</div>
                    <div className="text-[10px] text-ink-subtle">{c.opened?.split(" ").slice(0,3).join(" ")}</div>
                  </div>
                </Link>
              ))}
            </div>
          </Card>

          <Card>
            <CardHeader title={`My alerts · ${myAlerts.length}`} right={<Link to="/alerts" className="text-[11px] text-amber-600 hover:underline">All →</Link>} />
            <div>
              {myAlerts.length === 0 ? (
                <EmptyState message={isSenior ? "Senior oversight role — see escalations below." : "No alerts assigned to you currently."} />
              ) : myAlerts.map(a => (
                <Link key={a.id} to={`/alerts/${a.id}`} className="flex items-start gap-3 p-3 border-b border-line/50 last:border-0 hover:bg-bg-hover">
                  <PriorityPill priority={a.priority} />
                  <div className="flex-1 min-w-0">
                    <div className="text-[13px] font-medium truncate">{a.subject_name}</div>
                    <div className="text-[11px] text-ink-subtle truncate">{a.id} · {a.rule}</div>
                  </div>
                  <StatusPill status={a.status} />
                </Link>
              ))}
            </div>
          </Card>
        </div>

        {/* Row 3: Pending approvals + SLA at risk + Today's schedule */}
        <div className="grid grid-cols-3 gap-3">
          <Card>
            <CardHeader title={`Pending my approval · ${pendingApprovals.length}`} subtitle="Maker-checker queue" />
            <div>
              {pendingApprovals.length === 0 ? <EmptyState message="No items waiting on your approval." /> : pendingApprovals.map(c => (
                <Link key={c.id} to={`/cases/${c.id}`} className="block p-3 border-b border-line/50 last:border-0 hover:bg-bg-hover">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-[12px] font-medium">{c.subject}</span>
                    <PriorityPill priority={c.priority} />
                  </div>
                  <div className="text-[11px] text-ink-subtle">{c.id} · proposed by {c.owner}</div>
                  <div className="flex gap-1.5 mt-2">
                    <button className="px-2 py-1 text-[10px] bg-risk-low text-white rounded">Approve</button>
                    <button className="px-2 py-1 text-[10px] border border-line rounded hover:bg-bg-hover">Reject</button>
                    <button className="px-2 py-1 text-[10px] border border-line rounded hover:bg-bg-hover">Request info</button>
                  </div>
                </Link>
              ))}
            </div>
          </Card>

          <Card>
            <CardHeader title="SLA at risk" subtitle="Items approaching breach" right={<Clock size={13} className="text-risk-veryhigh" />} />
            <div>
              {slaAtRisk.length === 0 ? <EmptyState message="All items within SLA." /> : slaAtRisk.map(s => (
                <Link key={s.id} to={`/cases/${s.id}`} className="flex items-center gap-3 p-3 border-b border-line/50 last:border-0 hover:bg-bg-hover">
                  <div className={`w-1.5 h-10 rounded-full ${s.breach ? "bg-risk-veryhigh" : "bg-risk-high"}`} />
                  <div className="flex-1 min-w-0">
                    <div className="text-[12px] font-medium truncate">{s.subject}</div>
                    <div className="text-[11px] text-ink-subtle">{s.id}</div>
                  </div>
                  <div className={`text-[11px] font-medium tabular ${s.breach ? "text-risk-veryhigh" : "text-risk-high"}`}>
                    {s.breach ? "BREACH" : `${s.hours_left}h left`}
                  </div>
                </Link>
              ))}
            </div>
          </Card>

          <Card>
            <CardHeader title="Today's schedule" subtitle="Calendar + recurring tasks" />
            <div>
              {schedule.map((e, i) => (
                <div key={i} className="flex items-start gap-3 p-3 border-b border-line/50 last:border-0">
                  <div className="text-[11px] font-medium tabular text-ink-muted w-10 pt-0.5">{e.time}</div>
                  <div className="flex-1 min-w-0">
                    <div className="text-[12px] font-medium">{e.title}</div>
                    <div className="text-[10px] text-ink-subtle">{e.type} · {e.with}</div>
                  </div>
                </div>
              ))}
            </div>
          </Card>
        </div>

        {/* Senior-only oversight row */}
        {isSenior && (
          <div className="grid grid-cols-3 gap-3">
            <Card>
              <CardHeader title={`P1 oversight · ${oversightP1.length}`} subtitle="All active P1 cases across the workspace" right={<Link to="/cases" className="text-[11px] text-amber-600 hover:underline">All P1 →</Link>} />
              <div>
                {oversightP1.slice(0, 6).map(c => (
                  <Link key={c.id} to={`/cases/${c.id}`} className="flex items-start gap-3 p-3 border-b border-line/50 last:border-0 hover:bg-bg-hover">
                    <PriorityPill priority={c.priority} />
                    <div className="flex-1 min-w-0">
                      <div className="text-[12px] font-medium truncate">{c.subject}</div>
                      <div className="text-[11px] text-ink-subtle truncate">{c.owner} · {c.status}</div>
                    </div>
                    <div className="text-[11px] tabular text-ink-muted">{formatNGN(c.amount,{short:true})}</div>
                  </Link>
                ))}
              </div>
            </Card>

            <Card>
              <CardHeader title={`Awaiting MLRO · ${awaitingMlro.length}`} subtitle="In MLRO review queue" />
              <div>
                {awaitingMlro.slice(0, 6).map(c => (
                  <Link key={c.id} to={`/cases/${c.id}`} className="flex items-start gap-3 p-3 border-b border-line/50 last:border-0 hover:bg-bg-hover">
                    <PriorityPill priority={c.priority} />
                    <div className="flex-1 min-w-0">
                      <div className="text-[12px] font-medium truncate">{c.subject}</div>
                      <div className="text-[11px] text-ink-subtle truncate">{c.id} · {c.status}</div>
                    </div>
                  </Link>
                ))}
              </div>
            </Card>

            <Card>
              <CardHeader title={`Escalations · ${escalations.length}`} subtitle="Alerts marked Escalated" />
              <div>
                {escalations.slice(0, 6).map(a => (
                  <Link key={a.id} to={`/alerts/${a.id}`} className="flex items-start gap-3 p-3 border-b border-line/50 last:border-0 hover:bg-bg-hover">
                    <PriorityPill priority={a.priority} />
                    <div className="flex-1 min-w-0">
                      <div className="text-[12px] font-medium truncate">{a.subject_name}</div>
                      <div className="text-[11px] text-ink-subtle truncate">{a.id} · {a.rule}</div>
                    </div>
                    <StatusPill status={a.status} />
                  </Link>
                ))}
              </div>
            </Card>
          </div>
        )}

        {/* Activity feed */}
        <Card>
          <CardHeader title="Recent activity in your workspace" subtitle="Audit-grade events across the platform" right={<Link to="/governance/audit-log" className="text-[11px] text-amber-600 hover:underline">Full log →</Link>} />
          <div className="grid grid-cols-3 divide-x divide-line/50">
            {audit.slice(0, 12).map((a, i) => (
              <div key={a.id || i} className="p-3 text-[11px] border-b border-line/50">
                <div className="flex items-baseline justify-between gap-2">
                  <span className="font-medium truncate">{a.user}</span>
                  <span className="text-ink-subtle text-[10px] shrink-0">{formatRelative(a.ts)}</span>
                </div>
                <div className="text-ink-subtle truncate mt-0.5">{a.action}</div>
                <div className="text-ink-subtle/80 text-[10px] truncate">{a.entity_type} · {a.entity_id}</div>
              </div>
            ))}
          </div>
        </Card>

        {/* Quick actions */}
        <Card>
          <CardHeader title="Quick actions" subtitle="Common workflows" right={<Activity size={13} className="text-amber-600" />} />
          <div className="p-4 grid grid-cols-4 gap-2">
            {[
              { to: "/screening", label: "Run sanctions screening", desc: "Single name or batch" },
              { to: "/customers", label: "Add new customer", desc: "Tier 1 / 2 / 3 onboarding" },
              { to: "/reporting/str", label: "Draft new STR", desc: "NFIU template" },
              { to: "/cases", label: "Open new case", desc: "Manual case creation" },
              { to: "/transactions", label: "Investigate transaction", desc: "Search by ID or customer" },
              { to: "/atom-explainer", label: "Ask Atom AI", desc: "Decision explainer" },
              { to: "/governance/audit-log", label: "Search audit log", desc: "Tamper-evident events" },
              { to: "/integrations", label: "Check integrations", desc: "Connection health" },
            ].map((q, i) => (
              <Link key={i} to={q.to} className="block p-3 border border-line rounded-xl hover:bg-bg-hover hover:border-amber/40 transition-colors">
                <div className="text-[12px] font-medium">{q.label}</div>
                <div className="text-[10px] text-ink-subtle mt-0.5">{q.desc}</div>
              </Link>
            ))}
          </div>
        </Card>
      </div>
    </div>
  );
}
